import React, { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Triangle, CheckCircle, XCircle, CreditCard, SealCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PublicProposal() {
  const { token } = useParams();
  const { lang, setLang } = useI18n();
  const [params, setParams] = useSearchParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [signName, setSignName] = useState("");
  const [polling, setPolling] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/public/proposals/${token}`);
      setP(r.data);
    } catch (e) {
      console.error("Failed to load public proposal:", e);
      setP(null);
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Poll on return from Stripe (runs once per mount when session_id present)
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    setPolling(true);
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      if (cancelled) return;
      attempts++;
      try {
        const r = await api.get(`/public/proposals/${token}/payment-status/${sid}`);
        if (r.data.payment_status === "paid") {
          toast.success(lang === "fr" ? "Acompte reçu !" : "Deposit received!");
          setPolling(false);
          params.delete("session_id");
          setParams(params, { replace: true });
          load();
          return;
        }
        if (attempts >= 8) { setPolling(false); return; }
        setTimeout(tick, 2000);
      } catch (e) {
        console.warn("Payment status poll failed:", e);
        setPolling(false);
      }
    };
    tick();
    return () => { cancelled = true; };
    // We intentionally run this once per mount when ?session_id= is present.
    // load/lang/params/setParams are stable enough; depending on `token` covers route changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const decide = async (decision) => {
    if (!signName.trim()) return toast.error(lang === "fr" ? "Nom requis pour signer" : "Name required to sign");
    setSigning(true);
    try {
      await api.post(`/public/proposals/${token}/decision`, { signed_name: signName, decision });
      toast.success(decision === "accepted" ? (lang === "fr" ? "Soumission acceptée" : "Proposal accepted") : (lang === "fr" ? "Soumission refusée" : "Proposal declined"));
      load();
    } catch { toast.error("Failed"); }
    finally { setSigning(false); }
  };

  const payDeposit = async () => {
    try {
      const r = await api.post(`/public/proposals/${token}/deposit-checkout`, { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Checkout failed"); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-zinc-500 label-eyebrow">Loading…</div>;
  if (!p) return <div className="min-h-screen flex items-center justify-center text-zinc-500 label-eyebrow">{lang === "fr" ? "Soumission introuvable" : "Proposal not found"}</div>;

  const accepted = p.status === "accepted" || p.status === "deposit_paid";
  const depositPaid = p.status === "deposit_paid";
  const declined = p.status === "declined";

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900">
      <div className="max-w-4xl mx-auto px-6 sm:px-10 py-10">
        {/* Header */}
        <header className="flex items-start justify-between gap-4 flex-wrap mb-10 pb-6 border-b-2 border-zinc-900">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-orange-600 flex items-center justify-center">
              <Triangle size={24} weight="fill" className="text-white" />
            </div>
            <div>
              <h1 className="font-display font-black text-2xl tracking-tighter">{p.company?.name || "C.H.C."}</h1>
              <p className="mono text-xs text-zinc-600 mt-1">{p.company?.owner} · {p.company?.phone}</p>
              <p className="mono text-xs text-zinc-600">{p.company?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="border border-zinc-300 inline-flex">
              {["en", "fr"].map(l => (
                <button key={l} onClick={() => setLang(l)} className={`label-eyebrow px-3 py-1.5 ${lang === l ? "bg-zinc-900 text-white" : "bg-white text-zinc-700"}`}>{l.toUpperCase()}</button>
              ))}
            </div>
            <Button variant="outline" onClick={() => window.print()} className="rounded-none border-zinc-300 uppercase font-bold tracking-wider text-xs h-10">PDF</Button>
          </div>
        </header>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
          <div>
            <p className="label-eyebrow text-zinc-500">{lang === "fr" ? "Soumission" : "Proposal"}</p>
            <p className="font-display font-bold text-lg mt-1">{p.title}</p>
            <p className="mono text-xs text-zinc-600 mt-1">{p.created_at?.slice(0,10)}</p>
          </div>
          <div>
            <p className="label-eyebrow text-zinc-500">{lang === "fr" ? "Pour" : "Prepared for"}</p>
            <p className="font-bold mt-1">{p.customer_name}</p>
            <p className="text-sm text-zinc-600">{p.customer_address}</p>
          </div>
          <div>
            <p className="label-eyebrow text-zinc-500">{lang === "fr" ? "Valide jusqu'au" : "Valid until"}</p>
            <p className="mono mt-1">{p.valid_until || "—"}</p>
          </div>
        </div>

        {p.scope && (
          <section className="mb-8">
            <p className="label-eyebrow text-zinc-500 mb-3">{lang === "fr" ? "Description des travaux" : "Scope of work"}</p>
            <p className="text-sm whitespace-pre-wrap leading-relaxed">{p.scope}</p>
          </section>
        )}

        {/* Line items */}
        <section className="border border-zinc-300 bg-white mb-8">
          <div className="grid grid-cols-12 px-4 py-3 border-b border-zinc-300 label-eyebrow text-zinc-500 bg-zinc-50">
            <div className="col-span-7">{lang === "fr" ? "Description" : "Item"}</div>
            <div className="col-span-1 text-right">Qty</div>
            <div className="col-span-2 text-right">$</div>
            <div className="col-span-2 text-right">{lang === "fr" ? "Total" : "Total"}</div>
          </div>
          {p.lines.map((l, i) => (
            <div key={i} className="grid grid-cols-12 px-4 py-3 border-b border-zinc-100 items-center text-sm">
              <div className="col-span-7 font-medium">{l.label} <span className="mono text-xs text-zinc-500 ml-2">{l.unit}</span></div>
              <div className="col-span-1 text-right mono">{l.qty}</div>
              <div className="col-span-2 text-right mono">{fmt$(l.unit_price)}</div>
              <div className="col-span-2 text-right mono font-semibold">{fmt$(l.total)}</div>
            </div>
          ))}
          <div className="px-4 py-3 border-b border-zinc-100 grid grid-cols-12">
            <div className="col-span-10 text-right label-eyebrow text-zinc-500">{lang === "fr" ? "Sous-total" : "Subtotal"}</div>
            <div className="col-span-2 text-right mono">{fmt$(p.subtotal)}</div>
          </div>
          <div className="px-4 py-3 border-b border-zinc-100 grid grid-cols-12">
            <div className="col-span-10 text-right label-eyebrow text-zinc-500">{lang === "fr" ? "Taxes" : "Tax"} ({p.tax_pct}%)</div>
            <div className="col-span-2 text-right mono">{fmt$(p.total - p.after_markup)}</div>
          </div>
          <div className="px-4 py-4 grid grid-cols-12 bg-zinc-900 text-white">
            <div className="col-span-10 text-right label-eyebrow">{lang === "fr" ? "Total" : "Total"}</div>
            <div className="col-span-2 text-right font-display font-black text-2xl text-orange-500">{fmt$(p.total)}</div>
          </div>
        </section>

        <section className="mb-10">
          <p className="label-eyebrow text-zinc-500 mb-3">{lang === "fr" ? "Conditions" : "Terms"}</p>
          <p className="text-xs text-zinc-700 leading-relaxed whitespace-pre-wrap">{p.terms}</p>
        </section>

        {/* Decision */}
        {declined && (
          <div className="border-2 border-red-600 p-6 flex items-center gap-3 bg-red-50">
            <XCircle size={28} className="text-red-600" weight="fill" />
            <div>
              <p className="font-bold text-red-900">{lang === "fr" ? "Soumission refusée" : "Proposal declined"}</p>
              <p className="text-xs mono text-red-700">{p.signed_name} · {p.signed_at?.slice(0,10)}</p>
            </div>
          </div>
        )}

        {accepted && (
          <div className="border-2 border-green-600 p-6 bg-green-50">
            <div className="flex items-center gap-3 mb-3">
              <SealCheck size={28} className="text-green-700" weight="fill" />
              <div>
                <p className="font-display font-bold text-lg text-green-900">{lang === "fr" ? "Soumission acceptée" : "Proposal accepted"}</p>
                <p className="text-xs mono text-green-700">{p.signed_name} · {p.signed_at?.slice(0,10)}</p>
              </div>
            </div>
            {depositPaid ? (
              <div className="border-l-4 border-green-700 pl-4 mt-4">
                <p className="label-eyebrow text-green-700">{lang === "fr" ? "ACOMPTE PAYÉ" : "DEPOSIT PAID"}</p>
                <p className="font-display font-black text-2xl text-green-900 mt-1">{fmt$(p.deposit_amount)}</p>
              </div>
            ) : (
              <div className="mt-4 border-t border-green-300 pt-4">
                <p className="text-sm text-green-900 mb-3">{lang === "fr" ? "Pour confirmer définitivement, payez l'acompte :" : "To lock in your booking, pay the deposit:"}</p>
                <Button data-testid="pay-deposit-button" onClick={payDeposit} className="rounded-none bg-orange-600 hover:bg-orange-700 text-white uppercase font-bold tracking-wider h-12 px-6">
                  <CreditCard size={16} className="mr-2" /> {lang === "fr" ? `Payer l'acompte de ${fmt$(p.deposit_amount)}` : `Pay ${fmt$(p.deposit_amount)} deposit`}
                </Button>
                {polling && <p className="mono text-xs text-orange-700 mt-3">{lang === "fr" ? "Vérification du paiement…" : "Checking payment…"}</p>}
              </div>
            )}
          </div>
        )}

        {!accepted && !declined && (
          <div className="border-2 border-zinc-900 p-6 bg-white">
            <p className="label-eyebrow text-zinc-700 mb-4">{lang === "fr" ? "Signez ici pour accepter ou refuser" : "Sign below to accept or decline"}</p>
            <Label className="label-eyebrow text-zinc-600 mb-2 block">{lang === "fr" ? "Votre nom complet" : "Your full name"}</Label>
            <Input
              data-testid="sign-name-input"
              value={signName}
              onChange={(e) => setSignName(e.target.value)}
              placeholder={lang === "fr" ? "Tapez votre nom" : "Type your name"}
              className="rounded-none font-mono mb-4 h-12 text-base"
            />
            <div className="flex flex-col sm:flex-row gap-3">
              <Button data-testid="accept-button" disabled={signing} onClick={() => decide("accepted")} className="rounded-none bg-green-700 hover:bg-green-800 text-white uppercase font-bold tracking-wider h-12 flex-1">
                <CheckCircle size={16} className="mr-2" weight="fill" /> {lang === "fr" ? "Accepter" : "Accept proposal"}
              </Button>
              <Button data-testid="decline-button" disabled={signing} onClick={() => decide("declined")} variant="outline" className="rounded-none border-zinc-400 hover:bg-zinc-900 hover:text-white uppercase font-bold tracking-wider h-12 flex-1">
                <XCircle size={16} className="mr-2" /> {lang === "fr" ? "Refuser" : "Decline"}
              </Button>
            </div>
          </div>
        )}

        <footer className="mt-16 pt-6 border-t border-zinc-200 mono text-[10px] text-zinc-500 text-center">
          {p.company?.name} · {p.company?.phone} · {p.company?.email}
        </footer>
      </div>
    </div>
  );
}
